import type { ReactNode } from 'react';

/* Angular parity — `critical-notifications.component.ts:64-68` +
 * `utils/card-expiry.ts:11-27 updateAndRearrangeNotifications`.
 *
 * The BE ships critical notifications with generic strings. Three
 * known types are rewritten client-side to the copy the design
 * system ships, and the same three float to the top of the list in
 * that order. Notifications the BE returns but the client doesn't
 * override are kept as-is in their original BE order after the
 * overridden trio.
 *
 * Copy pulled from `assets/locales/translation/en.yaml`:
 *   creditCardExpiry / achToCreditcard / proprietaryToCreditcard. */

export type CriticalNotificationOverride = {
  /** BE `notification.type` value to match. */
  type: string;
  /** HTML string rendered via `dangerouslySetInnerHTML`. Kept as an
   *  HTML string (not JSX) so the same string flows through
   *  DOMPurify like BE messages do. */
  message: string;
};

const UPDATE_TODAY_LINK =
  '<a href="/my-account/plan-details" class="alertLinkText">Update today</a>';

/* Order matters — matched entries are floated to the top of the
 * banner list in this exact sequence. */
export const CRITICAL_NOTIFICATION_OVERRIDES: CriticalNotificationOverride[] = [
  {
    type: 'ach-to-creditcard',
    message: `Your payment method on file needs to be updated. ${UPDATE_TODAY_LINK}`,
  },
  {
    type: 'proprietary-to-creditcard',
    message: `Your payment method on file needs to be updated. ${UPDATE_TODAY_LINK}`,
  },
  {
    type: 'credit-card-expiring',
    message: `Your credit card on file expires soon. ${UPDATE_TODAY_LINK}`,
  },
];

/* Angular's list is `[ach, proprietary, credit-card-expiring]`
 * (order preserved), but the component only renders the FIRST
 * notification at a time. Kept the sequence identical so the
 * "first notification wins" behaviour matches Angular byte-for-byte
 * if we ever gate on that. */

/* Union type so consumers can accept a JSX children slot too. */
export type CriticalNotificationMessage = string | ReactNode;
